import type { ImageProcessingConfig } from '@/types/imageProcessing'
import { MAX_SIZE_WITHOUT_COMPRESSION_MB, MAX_ORIGINAL_IMAGE_SIZE_MB } from '@/config/imageSize'

/**
 * Single source of truth for client-side image validation/compression tunables.
 *
 * Three-tier model for AI inference path (OCR/Info-Label uses separate 2 MB flat limit):
 * - Tier 1 (≤ 5 MB): Upload untouched, no compression — preserves quality for AI.
 * - Tier 2 (> 5 MB, ≤ 10 MB): Compress via retry ladder, target final size ≤ 5 MB.
 * - Tier 3 (> 10 MB): Reject immediately with error — no compression attempt.
 *
 * `maxSizeMB` is the Tier 1 threshold (skip compression); `maxOriginalSizeMB` is the
 * Tier 3 absolute ceiling (pre-rejection).
 *
 * `maxPixels` (optional) enables early dimension/pixel validation before compression.
 * `compressionTimeoutMs` terminates compression if it exceeds this duration.
 */
export const defaultImageProcessingConfig: ImageProcessingConfig = {
  maxSizeMB: MAX_SIZE_WITHOUT_COMPRESSION_MB,
  maxOriginalSizeMB: MAX_ORIGINAL_IMAGE_SIZE_MB,
  maxPixels: 30_720_000, // Safety margin: ~2400x2560
  compressionTimeoutMs: 30_000, // 30 seconds
  maxWidthOrHeight: 2400,
  initialQuality: 0.8,
  minQuality: 0.4,
  qualityStep: 0.1,
  dimensionStep: 0.85,
  minWidthOrHeight: 800,
  maxRetries: 4,
  maxConcurrentCompressions: 2,
}

export const SUPPORTED_IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png'])
export const SUPPORTED_IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png'])